/**
 * @file useSiteBuilderState.ts
 * @description Состояние конструктора сайта для `BuilderContext`
 * (phase19.16 Р1-6а): выбор в адресе, страницы и действия с блоками, один
 * iframe канвы, источник канвы, окна «Новая страница» и «Сохранить как
 * дизайн», раскладка по ширине окна, права на страницы.
 *
 * Пока открыта страница, она стоит в окне публикации вида сайта пунктом
 * «Страница «…»» (`setPageTarget`).
 *
 * phase19.16 Р2: история «Отменить / Вернуть» на открытую страницу
 * (сбрасывается при смене страницы), выбранный элемент секции, окна
 * «Добавить блок» (с местом вставки) и «Сделать главной».
 *
 * phase19.16 Р2а: экран «Страница товара» — шаблон из блоков (замки
 * обязательных элементов — `useTemplateGuards`; публикуется с видом сайта,
 * поэтому пунктом «Страница» в окно публикации не встаёт, а после правок
 * перечитывается «что изменено»); что показать на канве каталога и шаблона
 * — категория и товар для примера (`canvasTarget`).
 *
 * phase19.16 Р3а: черновик и публикация открытой страницы
 * (`usePagePublishing`), окна «Публикации», «Все версии», «Вернуть версию»,
 * «Удалённые страницы» (`usePublicationsUi`); в окне публикации открытая
 * страница — пунктом по умолчанию (`setPageTarget`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBlockActions, type FormSink } from "./useBlockActions";
import { useBuilderPages } from "./useBuilderPages";
import { useBuilderSelection } from "./useBuilderSelection";
import { usePagePublishing } from "./usePagePublishing";
import { usePublicationsUi, type PublicationsUi } from "./usePublicationsUi";
import { useTemplateGuards } from "./useTemplateGuards";
import { useDesigns } from "./useDesigns";
import { useUndoHotkeys, useUndoStack } from "./useUndoStack";
import { useHasPermission } from "../../auth/hooks";
import { previewFocusFor } from "../../lib/site-builder/canvas";
import type { BuilderContextValue, BuilderLayout, CanvasOverride, CanvasTarget, ElementSel, LibraryOptions } from "../../components/site-builder/builder-context";
import { blockErrorText } from "../../lib/site-builder/block-errors";
import { useSiteView } from "../../components/site-view/site-view-context";
import { useNotify } from "../../components/ui/NotifyProvider";
import { useConfirm, type ConfirmApi } from "../../components/ui/use-confirm";
import { PHONE_QUERY, WIDE_QUERY, useMediaQuery } from "../useMediaQuery";

export interface SiteBuilderState {
  value: BuilderContextValue;
  confirmApi: ConfirmApi;
  saveDesignOpen: boolean;
  setSaveDesignOpen: (open: boolean) => void;
  addPageOpen: boolean;
  /** Дизайн или версия на весь экран («Весь сайт ↗» при снимке на канве). */
  wholeSite: CanvasOverride | null;
  closeWholeSite: () => void;
  setAddPageOpen: (open: boolean) => void;
  /** Окно «Добавить блок»: открыто и куда вставлять (`null` — в начало, `undefined` — в конец). */
  library: ({ after: string | null | undefined } & LibraryOptions) | null;
  closeLibrary: () => void;
  /** Окно «Сделать главной»: id страницы или `null`. */
  setHomePageId: string | null;
  closeSetHome: () => void;
  /** Р3а: окна черновика страницы. */
  publicationsUi: PublicationsUi;
}

export function useSiteBuilderState(): SiteBuilderState {
  const { selection, select, setGuard, leave } = useBuilderSelection();
  const canEditPages = useHasPermission("SHOP_MANAGE_CONTENT");
  // Без права на контент страницы не запрашиваем (403) — «Весь сайт» доступен.
  const pages = useBuilderPages(selection.pageId, selection.screen, canEditPages);
  const confirmApi = useConfirm();
  const notify = useNotify();
  const view = useSiteView();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [override, setOverride] = useState<CanvasOverride | null>(null);
  const [saveDesignOpen, setSaveDesignOpen] = useState(false);
  const [addPageOpen, setAddPageOpen] = useState(false);
  const [wholeSite, setWholeSite] = useState<CanvasOverride | null>(null);
  const deviceChosen = useRef(false);
  const canPublishPages = useHasPermission("SHOP_PUBLISH_CONTENT");
  const canEditSettings = useHasPermission("SHOP_MANAGE_SETTINGS");
  const designs = useDesigns(canEditSettings);
  const phone = useMediaQuery(PHONE_QUERY);
  const wide = useMediaQuery(WIDE_QUERY);
  const layout: BuilderLayout = phone ? "phone" : wide ? "desktop" : "tablet";

  // Свежий выбор для снятия выбора удалённого блока.
  const latestSel = useRef(selection.sel);
  latestSel.current = selection.sel;
  const onBlockGone = useCallback(
    (id: string) => {
      const sel = latestSel.current;
      if (sel.kind === "block" && sel.id === id) void select({ sel: { kind: "page" } });
    },
    [select],
  );
  const undo = useUndoStack({
    resetKey: pages.page?.id ?? null,
    onError: (err, kind) => notify.error(blockErrorText(err, kind === "undo" ? "Не удалось отменить." : "Не удалось вернуть.")),
  });
  useUndoHotkeys(undo, canEditPages && (selection.screen === null || selection.screen === "product"));
  const formSink = useRef<FormSink | null>(null);
  const rawBlocks = useBlockActions({ pages, confirm: confirmApi.confirm, notify, undo, onBlockGone, formSink });
  const blocks = useTemplateGuards(rawBlocks, { pages, confirm: confirmApi.confirm, notify, onBlockGone, undo });
  // Р3а: «Отменить правки» и возврат версии переписывают блоки — история «Отменить» устарела.
  const publishing = usePagePublishing(pages, { enabled: canEditPages, onDraftReplaced: undo.clear });
  const publicationsUi = usePublicationsUi({
    layout,
    pageId: pages.page?.id ?? null,
    canRollback: canPublishPages && canEditPages,
    select,
    override,
    setOverride,
  });
  const [canvasTarget, setCanvasTarget] = useState<CanvasTarget>({ category: null, product: null });
  const [element, setElement] = useState<ElementSel | null>(null);
  const [library, setLibrary] = useState<({ after: string | null | undefined } & LibraryOptions) | null>(null);
  const [setHomePageId, setSetHomePageId] = useState<string | null>(null);
  const [bgPhotoRequest, setBgPhotoRequest] = useState<string | null>(null);
  // Место вставки по умолчанию — после выбранного блока, иначе в конец.
  const openLibrary = useCallback(
    (after?: string | null, options: LibraryOptions = {}) => {
      const sel = latestSel.current;
      setLibrary({ ...options, after: after !== undefined ? after : sel.kind === "block" ? sel.id : undefined });
    },
    [],
  );

  // Выбранного блока больше нет на странице (отменили добавление, удалили в
  // другой вкладке) — справа настройки страницы, а не «Блок не найден».
  const loadedPage = pages.page;
  useEffect(() => {
    const sel = latestSel.current;
    if (!loadedPage || sel.kind !== "block") return;
    if (!loadedPage.blocks.some((b) => b.id === sel.id)) void select({ sel: { kind: "page" } });
  }, [loadedPage, select]);

  // Открытая страница — пунктом окна публикации (отмечена по умолчанию, SB105).
  const { page } = pages;
  const { setPageTarget, refreshChanges } = view;
  const { afterChange } = publishing;
  const openPageId = page && page.kind !== "TEMPLATE" ? page.id : null;
  // «Показать на сайте» с правками — окно публикации с отмеченным показом (одноразовый признак).
  const showIntent = useRef(false);
  const takeShowIntent = useCallback(() => {
    const value = showIntent.current;
    showIntent.current = false;
    return value;
  }, []);
  const { openDialog } = view;
  const openPublishToShow = useCallback(() => {
    showIntent.current = true;
    openDialog("publish");
  }, [openDialog]);
  useEffect(() => {
    setPageTarget({ pageId: openPageId, canPublish: canPublishPages, canView: canEditPages, afterPublish: afterChange, takeShowIntent });
    return () => setPageTarget(null);
  }, [openPageId, afterChange, canPublishPages, canEditPages, setPageTarget, takeShowIntent]);
  // Правка блоков шаблона меняет черновик вида сайта — «Не опубликовано: шаблон товара N».
  useEffect(() => {
    if (page?.kind === "TEMPLATE") refreshChanges();
  }, [page, refreshChanges]);

  // «Весь сайт ↗» открывает то же, что на канве: страницу, экран или ленту блога.
  const { setScreen, setTarget } = view.preview;
  const openWholeSite = useCallback(() => {
    const focus = previewFocusFor(selection, page);
    setScreen(focus.screen);
    if (focus.target) setTarget(focus.target);
    // Версию страницы на весь экран пока не открыть (предпросмотр берёт адрес по экрану) — подсказка.
    if (override?.pageVersion) notify.info("Версию страницы можно посмотреть только на канве — на весь экран она пока не открывается.");
    else if (override) setWholeSite(override);
    else openDialog("preview");
  }, [selection, page, override, setScreen, setTarget, openDialog, notify]);

  const value = useMemo<BuilderContextValue>(
    () => ({
      selection,
      select,
      setGuard,
      leave,
      pages,
      blocks,
      iframeRef,
      override,
      setOverride,
      confirm: confirmApi.confirm,
      layout,
      canEditPages,
      canPublishPages,
      canEditSettings,
      designs,
      openSaveDesign: () => setSaveDesignOpen(true),
      openAddPage: () => setAddPageOpen(true),
      openWholeSite,
      deviceChosen,
      undo,
      element,
      setElement,
      openLibrary,
      openSetHome: setSetHomePageId,
      publishing,
      openPublications: publicationsUi.openPublications,
      openVersions: publicationsUi.openVersions,
      openDeletedPages: publicationsUi.openDeletedPages,
      openPublishToShow,
      viewPageVersion: publicationsUi.viewPageVersion,
      bgPhotoRequest,
      setBgPhotoRequest,
      formSink,
      canvasTarget,
      setCanvasTarget,
    }),
    [selection, select, setGuard, leave, pages, blocks, override, confirmApi.confirm, layout, canEditPages, canPublishPages, canEditSettings, designs, openWholeSite, undo, element, openLibrary, bgPhotoRequest, canvasTarget, publishing, publicationsUi.openPublications, publicationsUi.openVersions, publicationsUi.openDeletedPages, publicationsUi.viewPageVersion, openPublishToShow],
  );

  return {
    value,
    confirmApi,
    saveDesignOpen,
    setSaveDesignOpen,
    addPageOpen,
    setAddPageOpen,
    wholeSite,
    closeWholeSite: () => setWholeSite(null),
    library,
    closeLibrary: () => setLibrary(null),
    setHomePageId,
    closeSetHome: () => setSetHomePageId(null),
    publicationsUi,
  };
}
